import React from 'react';
import { Card, Row, Col, Typography, Space, Select, DatePicker, message, InputNumber, Input } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { LeftOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';
import { useGetMatWarehouses, useGetMatGapWhInventory } from '../../../hooks/genericHooks';
import type { MatGapTxn, MatWarehouses } from '../../../types';
import { fetchSqlData, fetchSqlDataWithQuery, insertData, getTableNextSequenceValue } from '../../../services/api';
import MatGapMasterMaterialSelect from '../../../components/modals/MatGapMasterMaterialSelect';
import StandardTable from '../../../components/common/StandardTable';
import { useAuth } from '../../../components/common/security/AuthContext';
import Button from '../../../components/common/Button';

const { Title, Text } = Typography;

type ReturnRow = MatGapTxn & { key: string; issued_or_assigned_qty: number; already_returned_qty: number; returnable_qty: number };

export default function MatGapTxnReturn() {
  const navigate = useNavigate();

  // Controls
  const [returnType, setReturnType] = React.useState<'issue' | 'assign'>('issue');
  const [selectedRef, setSelectedRef] = React.useState<string | undefined>(undefined);
  const [returnDate, setReturnDate] = React.useState<string | null>(dayjs().format('YYYY-MM-DD'));
  const { data: whData = [] } = useGetMatWarehouses() as any;
  const warehouses: MatWarehouses[] = Array.isArray(whData) ? whData : (whData?.data ?? []);
  const { user: currentUser } = useAuth();
  const [toWhId, setToWhId] = React.useState<number | undefined>(undefined);
  const [notes, setNotes] = React.useState<string>('');
  // Material filter (optional)
  const [selectedMatId, setSelectedMatId] = React.useState<string | null>(null);
  const [selectedMatLabel, setSelectedMatLabel] = React.useState<string>('');
  const [matModalOpen, setMatModalOpen] = React.useState(false);

  // Base code derived from original reference (issue: issued_to, assign: assignee_id)
  const [baseIssuedToCode, setBaseIssuedToCode] = React.useState<string>('');

  // Owned warehouses set for current user (destination)
  const ownedWhSet = React.useMemo(() => {
    const s = new Set<number>();
    (warehouses || []).forEach(w => {
      const owners: string[] = Array.isArray(w?.owners) ? (w.owners as string[]) : [];
      if (currentUser && owners.includes(String(currentUser))) s.add(Number(w.id));
    });
    return s;
  }, [warehouses, currentUser]);
  const whOptions = React.useMemo(() => (warehouses || []).map(w => ({ value: w.id, label: w.name, disabled: !ownedWhSet.has(Number(w.id)) })), [warehouses, ownedWhSet]);
  React.useEffect(() => {
    if (toWhId != null && !ownedWhSet.has(Number(toWhId))) setToWhId(undefined);
  }, [ownedWhSet, toWhId]);

  // Data
  const [allTxns, setAllTxns] = React.useState<MatGapTxn[]>([]); // issue + assign for refs
  const [loading, setLoading] = React.useState(false);

  // For descriptions: use inventory view map mat_id -> description
  const { data: invData = [], refetch: refetchInventory } = useGetMatGapWhInventory() as any;
  const descMap = React.useMemo(() => {
    const m = new Map<string, string | null>();
    const rows = Array.isArray(invData) ? invData : (invData?.data ?? []);
    (rows || []).forEach((r: any) => { const k = String(r.mat_id); if (!m.has(k)) m.set(k, r.description || null); });
    return m;
  }, [invData]);

  // Load refs for issue/assign
  const [refOptions, setRefOptions] = React.useState<{ value: string; label: string }[]>([]);

  React.useEffect(() => {
    const loadRefs = async () => {
      try {
        setLoading(true);
        // Get distinct reference_no for the chosen type
        const matFilter = selectedMatId != null ? ` and mat_id = ${Number(selectedMatId)}` : '';
        const q = `select reference_no from mat_gap_txns where op_type='${returnType}' and reference_no is not null${matFilter} group by reference_no order by reference_no`;
        const refRows = await fetchSqlDataWithQuery(q);
        const opts = (Array.isArray(refRows) ? refRows : (refRows?.data ?? [])).map((r: any) => ({ value: String(r.reference_no), label: String(r.reference_no) }));
        setRefOptions(opts);
      } catch (e) {
        console.error(e);
        message.error('Failed to load references');
      } finally {
        setLoading(false);
      }
    };
    loadRefs();
    setSelectedRef(undefined);
  }, [returnType, selectedMatId]);

  // Load transactions for selected reference
  React.useEffect(() => {
    const loadTxns = async () => {
      if (!selectedRef) { setAllTxns([]); return; }
      try {
        setLoading(true);
        // Base rows (issued or assigned)
        const matFilter = selectedMatId != null ? ` and mat_id = ${Number(selectedMatId)}` : '';
        const baseQ = `select id, op_type, txn_date, mat_id, qty, unit, from_warehouse_id, to_warehouse_id, reference_no, assignee_type, assignee_id\n          from mat_gap_txns where op_type='${returnType}' and reference_no='${selectedRef.replace(/'/g, "''")}'${matFilter}`;
        const baseRes = await fetchSqlDataWithQuery(baseQ);
        const baseRows: MatGapTxn[] = Array.isArray(baseRes) ? baseRes : (baseRes?.data ?? []);

        // Sum of already returned per mat_id for this original reference (issued_to stores REF:CODE)
        const retQ = `select mat_id, coalesce(sum(qty),0) as returned_qty\n          from mat_gap_txns where op_type='return' and issued_to like '${selectedRef.replace(/'/g, "''")}:%' group by mat_id`;
        console.log('ret_qty', retQ);
        const retRes = await fetchSqlDataWithQuery(retQ);
        const retRows: any[] = Array.isArray(retRes) ? retRes : (retRes?.data ?? []);
        const returnedByMat = new Map<number, number>();
        retRows.forEach(r => returnedByMat.set(Number(r.mat_id), Number(r.returned_qty)));

        // Build combined
        const rows: MatGapTxn[] = baseRows;
        setAllTxns(rows);

        // Prepare return rows
        const enriched: ReturnRow[] = rows.map(r => {
          const issuedOrAssigned = Number(r.qty || 0);
          const alreadyReturned = Number(returnedByMat.get(Number(r.mat_id)) || 0);
          const returnable = Math.max(0, issuedOrAssigned - alreadyReturned);
          return { ...r, key: String(r.id), issued_or_assigned_qty: issuedOrAssigned, already_returned_qty: alreadyReturned, returnable_qty: returnable };
        });
        setTableData(enriched);

        // Derive original code per selected reference
        if (returnType === 'issue') {
          const issuedToRes = await fetchSqlDataWithQuery(`select issued_to from mat_gap_txns where op_type='issue' and reference_no='${selectedRef.replace(/'/g, "''")}' limit 1`);
          const issuedToRow: any = Array.isArray(issuedToRes) ? issuedToRes[0] : (issuedToRes?.data?.[0] ?? null);
          const issuedToVal: string = String(issuedToRow?.issued_to || '').trim();
          const code = issuedToVal.split(' - ')[0] || '';
          setBaseIssuedToCode(code);
        } else if (returnType === 'assign') {
          const assigneeRes = await fetchSqlDataWithQuery(`select assignee_id from mat_gap_txns where op_type='assign' and reference_no='${selectedRef.replace(/'/g, "''")}' limit 1`);
          const assigneeRow: any = Array.isArray(assigneeRes) ? assigneeRes[0] : (assigneeRes?.data?.[0] ?? null);
          const code = String(assigneeRow?.assignee_id || '').trim();
          setBaseIssuedToCode(code);
        } else {
          setBaseIssuedToCode('');
        }
      } catch (e) {
        console.error(e);
        message.error('Failed to load transactions for reference');
      } finally {
        setLoading(false);
      }
    };
    loadTxns();
  }, [selectedRef, returnType, selectedMatId]);

  const [tableData, setTableData] = React.useState<ReturnRow[]>([]);
  const [returnQtyById, setReturnQtyById] = React.useState<Record<number, number>>({});

  const columns: ColumnsType<ReturnRow> = React.useMemo(() => [
    { title: 'Mat ID', dataIndex: 'mat_id', key: 'mat_id', width: 80 },
    { title: 'Description', key: 'description', width: 300, ellipsis: true, render: (_: any, r: ReturnRow) => descMap.get(String(r.mat_id)) || '' },
    { title: returnType === 'issue' ? 'Issued Qty' : 'Assigned Qty', dataIndex: 'issued_or_assigned_qty', key: 'issued_or_assigned_qty', width: 35, align: 'right' },
    { title: 'Returned', dataIndex: 'already_returned_qty', key: 'already_returned_qty', width: 35, align: 'right' },
    { title: 'Returnable', dataIndex: 'returnable_qty', key: 'returnable_qty', width: 35, align: 'right' },
    { title: 'Unit', dataIndex: 'unit', key: 'unit', width: 25 },
    {
      title: 'Return Qty', key: 'return_qty', width: 50, align: 'right',
      render: (_, r) => (
        <InputNumber
          min={0}
          max={r.returnable_qty || r.qty}
          value={returnQtyById[r.id] || 0}
          onChange={v => setReturnQtyById(prev => ({ ...prev, [r.id]: typeof v === 'number' ? v : 0 }))}
          style={{ width: 80 }}
        />
      )
    },
  ], [returnType, returnQtyById]);

  const handleSubmit = async () => {
    try {
      if (!selectedRef) { message.warning('Select a reference'); return; }
      if (!toWhId) { message.warning('Select destination warehouse'); return; }
      if (!returnDate) { message.warning('Select return date'); return; }

      // Build a friendly reference number for this return batch (RET-XXXXXXX)
      const nextId = await getTableNextSequenceValue('mat_gap_txns', 'id');
      if (!nextId) { message.error('Failed to get next transaction id'); return; }
      const retRef = `RET-${String(nextId).padStart(8, '0')}`;

      const payloads = tableData
        .filter(r => Number(returnQtyById[r.id] || 0) > 0)
        .map(r => ({
          op_type: 'return' as const,
          txn_date: returnDate,
          mat_id: r.mat_id,
          qty: Number(returnQtyById[r.id] || 0),
          unit: r.unit,
          to_warehouse_id: toWhId,
          reference_no: retRef,
          issued_to: (baseIssuedToCode ? `${selectedRef}:${baseIssuedToCode}` : undefined),
          notes: notes || undefined,
        }));

      if (payloads.length === 0) { message.warning('Enter quantities to return'); return; }

      await insertData('mat_gap_txns', payloads);
      try { await refetchInventory?.(); } catch {}
      message.success(`Return recorded: ${payloads.length} line(s). Ref: ${retRef}`);
      setReturnQtyById({});
      setSelectedRef(undefined);
      setNotes('');
    } catch (e: any) {
      console.error(e);
      message.error(e?.message || 'Failed to submit return');
    }
  };

  return (
    <Card bordered={false} bodyStyle={{ padding: 8 }}>
      <Row justify="space-between" align="middle" style={{ marginBottom: 12 }}>
        <Col>
          <Space align="center" size={8}>
            <Button icon={<LeftOutlined />} onClick={() => navigate(-1)} type="text" aria-label="Back" />
            <div>
              <Title level={4} style={{ margin: 0 }}>Return Materials</Title>
              <Text type="secondary">Return items by reference (Issue or Assign) back to a warehouse</Text>
            </div>
          </Space>
        </Col>
      </Row>

      <Row style={{ marginBottom: 12 }}>
        <Col span={24}>
          <Space wrap>
            <Select
              value={returnType}
              onChange={setReturnType}
              options={[{ value: 'issue', label: 'Issue Reference' }, { value: 'assign', label: 'Assign Reference' }]}
              style={{ width: 180 }}
            />
            <Input
              placeholder="Filter by Material (optional)"
              value={selectedMatLabel}
              allowClear
              onChange={(e) => {
                const v = e.target.value;
                setSelectedMatLabel(v);
                if (!v) { setSelectedMatId(null); setSelectedRef(undefined); }
              }}
              style={{ width: 320 }}
              addonAfter={<Button size="small" onClick={() => setMatModalOpen(true)}>Select</Button>}
            />
            <Button size="small" onClick={() => { setSelectedMatId(null); setSelectedMatLabel(''); setSelectedRef(undefined); }}>Clear</Button>
            <Select
              placeholder="Select Reference"
              allowClear
              showSearch
              value={selectedRef}
              onChange={setSelectedRef as any}
              options={refOptions}
              style={{ width: 280 }}
              filterOption={(input, option) => ((option?.label as string) || '').toLowerCase().includes((input || '').toLowerCase())}
            />
            <Select
              placeholder="Destination Warehouse"
              allowClear
              value={toWhId}
              onChange={(v) => setToWhId(v)}
              options={whOptions}
              style={{ width: 260 }}
            />
            <span>
              <Text strong>Return Date</Text>{' '}
              <DatePicker
                value={returnDate ? dayjs(returnDate) : null}
                onChange={(d) => setReturnDate(d ? d.format('YYYY-MM-DD') : null)}
                disabledDate={(d) => !!d && d.isAfter(dayjs(), 'day')}
              />
            </span>
            <Input.TextArea
              placeholder="Notes"
              value={notes}
              onChange={e => setNotes(e.target.value)}
              style={{ width: 320 }}
              rows={1}
            />
            <Button action="add" type="primary" onClick={handleSubmit} loading={loading}>Submit Return</Button>
          </Space>
        </Col>
      </Row>

      {selectedRef ? (
        <StandardTable<ReturnRow>
          columns={columns as any}
          dataSource={tableData}
          rowKey={(r) => r.key}
          loading={loading}
          pagination={{ pageSize: 20, showSizeChanger: true }}
          scroll={{ x: 'max-content' }}
        />
      ) : (
        <Card size="small"><Text type="secondary">Select a reference to see items.</Text></Card>
      )}

      {/* issued_to is derived from the original reference; no personnel picker */}

      {/* Master material picker modal (renders at root level) */}
      <MatGapMasterMaterialSelect
        open={matModalOpen}
        onCancel={() => setMatModalOpen(false)}
        onOk={(rows) => {
          const first = Array.isArray(rows) && rows.length > 0 ? rows[0] : null;
          if (first) {
            setSelectedMatId(String(first.mat_id));
            setSelectedMatLabel(`${first.mat_id} - ${first.description || ''}`);
          } else {
            setSelectedMatId(null);
            setSelectedMatLabel('');
          }
          setSelectedRef(undefined);
          setMatModalOpen(false);
        }}
        title="Select Material to Filter References"
        returnMode="full"
      />
    </Card>
  );
}
